import { flowRight } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import { connect } from '../../../common/components/runtime-context';
import PageCover from '../page-cover';
import { COVER_TYPE_COLOR } from '../../constants/cover-types';
import { TEXT_ALIGNMENT_CLASS_NAMES } from '../../constants/text-alignment';
import { IS_TOP_BAR_ENABLED_PATH } from '../../constants/wix-params';
import forDevice from '../../hoc/for-device';
import withAppSettings from '../../hoc/with-app-settings';
import styles from './page-description.scss';
import {
  getHeaderHeight,
  getMainPageHeaderHeight,
  getCategoryTextAlignment,
  getMainPageHeaderTextAlignment,
} from '../../selectors/app-settings-selectors';

const DEFAULT_HEIGHT = 200;

const getContainerStyle = (textColor = {}, headerHeight, isTopBarEnabled) => {
  const style = { color: textColor.color };
  if (headerHeight) {
    style.height = headerHeight;
  }
  if (!isTopBarEnabled) {
    style.marginTop = '20px';
  }
  return style;
};

export const PageDescription = props => {
  const {
    title,
    description,
    cover,
    coverType,
    overlayColor,
    backgroundColor,
    textColor = {},
    categoryTextAlignment,
    children,
    isCoverVisible,
    headerHeight,
    isTopBarEnabled = true,
  } = props;

  const textAlignmentClassName = styles[TEXT_ALIGNMENT_CLASS_NAMES[categoryTextAlignment]];
  const containerClassName = classNames(
    styles.container,
    'default-desktop-header-text-color',
    'page-description',
    {
      [textAlignmentClassName]: textAlignmentClassName,
    },
  );

  const NoCover = ({ children }) => <div>{children}</div>;

  const CoverComponent = isCoverVisible ? PageCover : NoCover;
  // if there is no image, any text and no background color, it would show empty white, block, so we hide it
  const hasNoContent =
    title === '' && description === '' && coverType === COVER_TYPE_COLOR && !backgroundColor;

  if (!isCoverVisible || hasNoContent) {
    return null;
  }

  return (
    <div
      className={containerClassName}
      style={getContainerStyle(textColor, headerHeight, isTopBarEnabled)}
    >
      <CoverComponent
        cover={cover}
        coverType={coverType}
        overlayColor={overlayColor}
        backgroundColor={backgroundColor}
        height={DEFAULT_HEIGHT}
        hasActiveState={false}
      >
        <div className={styles.wrapper}>
          <div
            className={classNames(
              styles.titleWrapper,
              'page-description-title-font',
              'page-description__title',
            )}
            data-hook="page-description__title"
          >
            <h1 className={styles.title}>{title}</h1>
          </div>
          <div
            className={classNames(styles.descriptionWrapper, 'page-description-font')}
            data-hook="page-description__description"
          >
            <p className={styles.description}>{description}</p>
          </div>
          {children}
        </div>
      </CoverComponent>
    </div>
  );
};

PageDescription.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  cover: PropTypes.object,
  coverType: PropTypes.string,
  overlayColor: PropTypes.object,
  backgroundColor: PropTypes.object,
  textColor: PropTypes.object,
  categoryTextAlignment: PropTypes.number.isRequired,
  children: PropTypes.node,
  isCoverVisible: PropTypes.bool.isRequired,
  headerHeight: PropTypes.number,
  isTopBarEnabled: PropTypes.bool,
  type: PropTypes.string,
};

PageDescription.defaultProps = {
  isCoverVisible: true,
};

const mapRuntimeToProps = state => ({
  headerHeight: getHeaderHeight(state),
  categoryTextAlignment: getCategoryTextAlignment(state),
});

export const PageDescriptionMobile = flowRight(connect(mapRuntimeToProps))(PageDescription);

export const PageDescriptionDesktop = flowRight(
  connect(mapRuntimeToProps),
  withAppSettings({
    isTopBarEnabled: IS_TOP_BAR_ENABLED_PATH,
  }),
)(PageDescription);

export default forDevice(PageDescriptionMobile, PageDescriptionDesktop);

const mapRuntimeToPropsMain = state => ({
  headerHeight: getMainPageHeaderHeight(state),
  categoryTextAlignment: getMainPageHeaderTextAlignment(state),
});

export const MainPageDescriptionMobile = flowRight(connect(mapRuntimeToPropsMain))(PageDescription);

export const MainPageDescriptionDesktop = flowRight(
  connect(mapRuntimeToPropsMain),
  withAppSettings({
    isTopBarEnabled: IS_TOP_BAR_ENABLED_PATH,
  }),
)(PageDescription);

export const MainPageDescription = forDevice(MainPageDescriptionMobile, MainPageDescriptionDesktop);
